import { useState, useCallback, useEffect } from "react"
import { createPortal } from "react-dom"
import { Source, Layer, useMap } from "react-map-gl/mapbox"
import type { LayerProps } from "react-map-gl/mapbox"
import type { FeatureCollection, Geometry, GeoJsonProperties } from "geojson"
import * as turf from "@turf/turf"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ElevationProfile } from "./elevation-profile"

type Point = [number, number]

const PRIMARY_COLOR = "#3b82f6"

const lineLayer: LayerProps = {
  id: "altimetry-line",
  type: "line",
  paint: {
    "line-color": PRIMARY_COLOR,
    "line-width": 3,
  },
}

const pointLayer: LayerProps = {
  id: "altimetry-point",
  type: "circle",
  paint: {
    "circle-radius": 6,
    "circle-color": PRIMARY_COLOR,
    "circle-stroke-width": 2,
    "circle-stroke-color": "#fff",
  },
}

export const Altimetry = () => {
  const { current: map } = useMap()
  const [points, setPoints] = useState<Point[]>([])
  const [elevations, setElevations] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [mapContainer, setMapContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setMapContainer(document.getElementById("explore-map-v2-root"))
  }, [])

  // Add DEM source and set terrain only once
  useEffect(() => {
    if (!map) return
    const mapbox = map.getMap ? map.getMap() : null
    if (!mapbox) return

    const trySetTerrain = () => {
      if (mapbox.getSource("mapbox-dem") && mapbox.isStyleLoaded() && mapbox.setTerrain) {
        mapbox.setTerrain({ source: "mapbox-dem", exaggeration: 1 })
      }
    }

    if (!mapbox.getSource("mapbox-dem")) {
      mapbox.addSource("mapbox-dem", {
        type: "raster-dem",
        url: "mapbox://mapbox.mapbox-terrain-dem-v1",
        tileSize: 512,
        maxzoom: 14,
      })
    }

    mapbox.on("styledata", trySetTerrain)
    mapbox.on("sourcedata", trySetTerrain)
    trySetTerrain()

    return () => {
      if (mapbox.setTerrain) {
        mapbox.setTerrain(null)
      }
      mapbox.off("styledata", trySetTerrain)
      mapbox.off("sourcedata", trySetTerrain)
      if (mapbox.getSource("mapbox-dem")) {
        try {
          mapbox.removeSource("mapbox-dem")
        } catch {
          // ignore if already removed
        }
      }
    }
  }, [map])

  const handleMapClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (points.length < 2) {
        setPoints([...points, [e.lngLat.lng, e.lngLat.lat]])
      }
    },
    [points],
  )

  // Fetch elevation profile when two points are set
  useEffect(() => {
    const fetchElevation = async () => {
      if (!map || points.length !== 2) return
      setLoading(true)

      const line = turf.lineString(points)
      const chunks = turf.lineChunk(line, 1, { units: "meters" }).features

      let elevations: number[] = []
      if (chunks.length > 0) {
        elevations = [
          ...chunks.map((feature) => {
            const coord = feature.geometry.coordinates[0] as [number, number]
            const elev = map.queryTerrainElevation(coord)
            return typeof elev === "number" ? elev : 0
          }),
          (() => {
            const lastChunk = chunks[chunks.length - 1]
            const lastCoord = lastChunk.geometry.coordinates[1] as [number, number]
            const elev = map.queryTerrainElevation(lastCoord)
            return typeof elev === "number" ? elev : 0
          })(),
        ]
      }

      setElevations(elevations)
      setLoading(false)
    }
    fetchElevation()
  }, [points, map])

  const handleReset = useCallback(() => {
    setPoints([])
    setElevations([])
    if (map) map.getCanvas().style.cursor = "crosshair"
  }, [map])

  const handleContextMenu = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      handleReset()
    },
    [handleReset],
  )

  const lineGeojson: FeatureCollection<Geometry, GeoJsonProperties> = {
    type: "FeatureCollection",
    features:
      points.length === 2
        ? [
            {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: points,
              },
              properties: {},
            },
          ]
        : [],
  }

  const pointsGeojson: FeatureCollection<Geometry, GeoJsonProperties> = {
    type: "FeatureCollection",
    features: points.map((pt) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: pt },
      properties: {},
    })),
  }

  useEffect(() => {
    if (!map) return

    const canvas = map.getCanvas()
    canvas.style.cursor = points.length < 2 ? "crosshair" : "not-allowed"
    map.on("click", handleMapClick)
    canvas.addEventListener("contextmenu", handleContextMenu)

    return () => {
      map.off("click", handleMapClick)
      canvas.removeEventListener("contextmenu", handleContextMenu)
      canvas.style.cursor = ""
    }
  }, [map, points, handleContextMenu])

  return (
    <>
      <Source id="altimetry-line" type="geojson" data={lineGeojson}>
        <Layer {...lineLayer} />
      </Source>
      <Source id="altimetry-points" type="geojson" data={pointsGeojson}>
        <Layer {...pointLayer} />
      </Source>
      {(loading || elevations.length > 0) &&
        mapContainer &&
        createPortal(
          <div className="absolute bottom-20 left-3 z-50 bg-white rounded-lg shadow-lg p-4">
            <Button variant="ghost" size="icon" onClick={handleReset} className="absolute top-2 right-2 z-[1] h-6 w-6">
              <X className="h-4 w-4" />
            </Button>
            {loading && <div className="text-sm text-gray-500 py-4 px-2">Chargement...</div>}
            <ElevationProfile elevations={elevations} points={points} />
          </div>,
          mapContainer,
        )}
    </>
  )
}

export default Altimetry
